/* eslint-disable max-depth */
/* eslint-disable max-lines */
/* eslint-disable react-func/max-lines-per-function */
import EventEmitter from 'events';
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@fuxi/eevee-icon';
import { useLocation } from 'react-router';
import { App } from '@capacitor/app';
import {
  useDocumentVisibility,
  useGetState,
  useLatest,
  useLockFn,
  useLongPress,
  useMemoizedFn,
  useUnmountedRef,
  useUpdateEffect,
} from 'ahooks';
import { Button, Dialog } from 'antd-mobile';
import { useDispatch, useSelector } from 'react-redux';
import { convertObjectKeysToCamelCase, TrackerCustomEvent, useTracker, ViewMemberInfoScene } from 'common-package';
import { Progress } from 'antd';
import Decimal from 'decimal.js';
import { Loading3QuartersOutlined } from '@ant-design/icons';
import lookie from 'lookie';
import {
  CheckArm,
  Header,
  Model2D,
  SlideSelect,
  Toast,
  RobotExceptionPrompt,
  PhysicalTherapyDrawer,
  HeaderMemberInfo,
  When,
} from '@/components';
import { useNav, useSocketIO } from '@/hooks';
import { selectCurrentServingOrder, setCurrentServingOrder } from '@/store/service';
import service from '@/service';
import { MatrixMoxibustionArea, Model2DRefAttrs, shoulderHaveDynamicActionsPlan } from '@/components/model-2d';
import {
  ALL_PAGES,
  CURRENT_ORDER_ID,
  MassageType2Text,
  SELECTED_CUSTOMER_ID,
  ShockwaveType,
  UPPER_STATUS_CODE,
  storagePrefixV2,
} from '@/constant';
import { formatSeconds, handleNavFromOrderInfo, isNewSoftwareVersion, uploadBase64Img } from '@/utils';
import { PhysicalTherapyDrawerRefAttrs } from '@/components/physical-therapy-drawer';
import { CheckArmRefAttrs } from '@/components/check-arm';
import toastify from '@/utils/toastify';
import disconnectImg from '@/assets/image/disconnect.png';
import { shouldUpgradeModelSolutions } from '@/components/model-2d/utils';
import { RobotExceptionPromptType } from '@/components/robot-exception-prompt';
import { MemberDrawer } from '@/pages/user/member-detail/member-drawer';
import { RobotExceptionEventsHandler } from '@/components/robot-exception-prompt/utils';
import { RF_TECH_SOLUTION_TO_STATIC_SOLUTION } from '../massage-setting/utils';
import ParamsAdjuster from './params-adjuster';
import HeatTip, { HeatTipRefAttrs } from './heat-tip';
import FuncAdjuster, { FuncAdjusterRefAttrs } from './func-adjuster';
import InstallMassageHead from './install-massage-head';
import Palpation from './palpation';

import cx from './index.module.scss';
import SlideImgs from './slide-imgs';
import MoxPlanDetails from './mox-plan-details';

interface Props {}

export const processManualImg = storagePrefixV2 + '/progress-manual.gif';

// 功能调节的参数项
const FunctionAdjustItems: Massage.ControlItemOptions[] = [
  'robot.broadcast',
  'robot.microphone',
  'robot.voice',
  'robot.step',
  'robot.bgm',
];

const MassageState2Text: Partial<Record<RobotState, string>> = {
  Running: '暂停',
  Running_Massaging: '暂停',
  Preparing_WaitingInstallation: '确认安装理疗末端',
  Preparing_HumanDetecting: '正在检测人体',
  Preparing_AcupointsAnalyzing: '穴位识别中',
  Pausing: '暂停中',
  Paused: '继续',
  Finishing: '结束中',
  Finished: '已结束',
  Preparing_AcupointsCalibration: '穴位矫正中',
};

// 理疗区域过小异常Code
const exposedAreaCode = [
  'ABDOMEN_FALSE_AREA',
  'LIMITED_ABDOMEN_EXPOSED_AREA',
  'LIMITED_SHOULDER_EXPOSED_AREA',
  'LIMITED_WAIST_EXPOSED_AREA',
  'LIMITED_BACK_EXPOSED_AREA',
];

const needCompatibilityCode = [
  'DETECTING_POSE_ERROR',
  'DETECTING_NOT_FLAT_LYING',
  'DETECTING_NOT_STAYING',
  'DETECTING_GOT_DISRUPTOR',
  'HAND_BLOCKING_MASSAGE_AREA',
  'DEPTH_MAP_ABNORMAL',
  'ABDOMEN_DISRUPTORS',
];

let apiCounters = 0; // 记录接口计数器
interface MassageControlProviderValue {
  allValues: Record<Massage.ControlItemOptions, number | [number, number]>;
  setAllValues: React.Dispatch<React.SetStateAction<MassageControlProviderValue['allValues']>>;
  getAllValues: () => MassageControlProviderValue['allValues'];
  massageState: RobotState;
  productInfo: GetProductResponse;
  currentMassageType: Massage.MassageType;
  isDisconnectMachineArm: boolean;
  currentStepText: string;
  isSocketConnected: boolean;
}

export const PalpationEventBus = new EventEmitter();
export const MassageControlValueContext = createContext<MassageControlProviderValue>({ allValues: {} } as any);

const MassageControl: React.FC<Props> = () => {
  const currentServingOrder = useSelector(selectCurrentServingOrder);
  const dispatch = useDispatch();
  const { sendEvent, recordLog } = useTracker();
  const { state } = useLocation();
  const documentVisibility = useDocumentVisibility();
  const documentVisibilityRef = useLatest(documentVisibility);
  const [isStartBtnDisabled, setIsStartBtnDisabled] = useState(true);
  const [allValues, setAllValues, getAllValues] = useGetState<MassageControlProviderValue['allValues']>({} as any);
  const [currentAdjustItem, setCurrentAdjustItem] = useState<'func' | 'params'>(state?.tab || 'params');
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [steps, setSteps, getSteps] = useGetState<GetMassageTaskInfoResponse[]>([]);
  const [bodyNodeList, setBodyNodeList] = useState([]);
  const [progressInfo, setProgressInfo] = useState<GetTaskProgressResponse>({} as GetTaskProgressResponse);
  const [controlPanelData, setControlPanelData, getControlPanelData] = useGetState<GetMassageControlPannelResponse>([]);
  const [isToggleTaskStateLoading, setIsToggleTaskStateLoading] = useState(false);
  const [isStoping, setIsStoping] = useState(false);
  const [storeMoxibustionCompletedAreas, setStoreMoxibustionCompletedAreas] =
    useState<GetMoxibustionCompletedAreasResponse['moxibustion_areas']>(null);
  const [massageTechValues, setMassageTechValues] = useState<CommonConfigResponse['values']>([]);
  const [currentStep, setCurrentStep, getCurrentStep] = useGetState(0);
  const [isTaskAlreadyStarted, setIsTaskAlreadyStarted] = useState(false);
  const [isRenderPointOK, setIsRenderPointOK] = useState(false);
  const [isRenderModelOK, setIsRenderModelOK] = useState(false);
  const [isRenderModelOKWithoutTimeout, setIsRenderModelOKWithoutTimeout, getIsRenderModelOKWithoutTimeout] =
    useGetState(false);
  const [storedHitTargetInfo, setStoredHitTargetInfo] = useState<GetShockwaveHitTargetInfo>(null);
  const [currentProcess, setCurrentProcess, getCurrentProcess] = useGetState<Service.SubStep>(null);
  const [isOrderError, setIsOrderError] = useState(false);
  const [isClickedEndMassage, setIsClickedEndMassage] = useGetState(false);
  const [isTaskAllCompleted, setIsTaskAllCompleted] = useState(false);
  const [massageState, setMassageState, getMassageState] = useGetState<RobotState>(null);
  const robotExceptionPromptRef = useRef<RobotExceptionPromptType>(null);
  const model2dRef = useRef<Model2DRefAttrs>(null);
  const [actionSteps, setActionSteps] = useState([]);
  const [isArmReconnecting, setIsArmReconnecting] = useState(false);
  const checkArmRef = useRef<CheckArmRefAttrs>(null);
  const isUnmount = useUnmountedRef();
  const isIgnoreTaskFinished = useRef(false);
  const isFirstUploadScreenshot = useRef(false);
  const isClickedEnd = useRef(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const [isShowPlanDetails, setIsShowPlanDetails] = useState(false);
  const heatTipRef = useRef<HeatTipRefAttrs>(null);
  const physicalTherapyDrawerRef = useRef<PhysicalTherapyDrawerRefAttrs>(null);
  const funcAdjusterRef = useRef<FuncAdjusterRefAttrs>(null);
  const [controlFooterContentShow, setControlFooterContentShow] = useState<'progress' | 'pause-tips'>(null);
  const latestOrder = useLatest<GetServiceOrderListResponse>(currentServingOrder);
  const [isUploadingScreenshot, setIsUploadingScreenshot] = useState(false);
  const [dynamicActions, setDynamicActions, getDynamicActions] = useGetState<Massage.DynamicActionConfig>([]);
  const [bgmState, setBgmState] = useState<GetMachineStateResponse['background_music']>(null);
  const [intensifyTreatmentStatus, setIntensifyTreatmentStatus] = useState<
    GetMachineStateResponse['intensify_treatment_status']
  >({} as any);
  const [productInfo, setProductInfo] = useState<GetProductResponse>(null);
  const isStepperValueChanging = useRef(false);
  const isStepperValueChangingTimer = useRef<NodeJS.Timeout>(null);
  const palpationTimeoutTimer = useRef(null);
  const skipBtnRef = useRef(null);
  const endBtnRef = useRef(null);
  const operateBtnRef = useRef(null);
  const [isDrawerShow, setIsDrawerShow] = useState<boolean>(false);
  const [customLineDrawInfo, setCustomLineDrawInfo] = useState<
    Parameters<Model2DRefAttrs['renderCustomLineCoords']>[0]
  >([]);
  const [currentCustomerInfo, setCurrentCustomerInfo] = useState<GetCustomerProfileResponse>(null);
  const navFunc = useNav();
  const [currentIsHasL1L2Error, setCurrentIsHasL1L2Error] = useState(false);

  const nav = useCallback(
    (...args: any) => {
      if (window.location.pathname !== '/service/massage-control') {
        return;
      }
      navFunc?.apply(this, args);
    },
    [navFunc]
  );
  // 总走线次数
  const [lineHits, setLineHits] = useState<number>(0);
  const [taskSequence, setTaskSequence] = useState<GetTaskSequenceStatus>(null);
  // 记录磁震波频率的之前值
  const previousShockwaveFrequency = useRef<number>(null);

  const [currentExposedAreaCode, setCurrentExposedAreaCode] = useState<MassageRuntime.RobotExceptionEvents>(null);

  const { socketInstance, initSocket, destroySocket } = useSocketIO({ autoCloseOnUnmount: false });

  // 当前方案是否是冲击波
  const currentStepIsShockwave = useMemo(() => steps[currentStep]?.massageItem === 'Shockwave', [currentStep, steps]);

  const isMatrizMoxibustionPlan = useMemo(
    () => currentServingOrder?.modelConfig?.find(item => item.value?.includes('matrix')),
    [currentServingOrder]
  );

  const currentModelSolution: Massage.ModelSolution = useMemo(() => {
    // return 'custom-draw-line';

    // 如果是腹部方案，手动设置人工方案模型图
    if (currentStep === 1 && currentServingOrder?.bodyPart === 'Abdomen') {
      return 'abdomen-RF';
    }
    // 兼容带手法的射频方案
    return RF_TECH_SOLUTION_TO_STATIC_SOLUTION[steps?.[currentStep]?.modelType] || steps?.[currentStep]?.modelType;
  }, [currentStep, steps, currentServingOrder?.bodyPart]);

  const currentOrderId = useMemo(() => {
    if (currentServingOrder?.orderId) {
      return currentServingOrder.orderId;
    } else {
      return lookie.get(CURRENT_ORDER_ID);
    }
  }, [currentServingOrder?.orderId]);
  const currentOrderIdRef = useLatest(currentOrderId);

  // 跳过按钮是否禁用
  const isSkipBtnDisabled = useMemo(
    () => isSkipping || massageState !== 'Running_Massaging' || progressInfo?.progress === 100,
    [isSkipping, massageState, progressInfo?.progress]
  );

  // 当前是否在打击线
  const currentIsHitLine = useMemo(
    () => storedHitTargetInfo?.currentHitTarget?.targetType === 'Route',
    [storedHitTargetInfo]
  );

  // 机器治疗下是否断开机械臂
  const isDisconnectMachineArm = useMemo(
    () => isArmReconnecting && steps?.[currentStep]?.massageType === 'Massage',
    [steps, currentStep, isArmReconnecting]
  );

  // 是否展示新的进度条交互
  const isShowNewProcess = useMemo(
    () =>
      taskSequence?.task_sequence?.length === 2 &&
      taskSequence?.task_sequence?.[0]?.state === 'Success' &&
      isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.7.0'),
    [taskSequence, currentServingOrder]
  );

  const isOperateBtnDisabled = useMemo(
    () =>
      currentIsHasL1L2Error ||
      !isSocketConnected ||
      isDisconnectMachineArm ||
      isClickedEndMassage ||
      steps?.length === 0 ||
      isStartBtnDisabled ||
      !['Running', 'Running_WaitingInstallation', 'Running_Massaging', 'Paused', 'Idle', null].includes(massageState) ||
      isTaskAllCompleted ||
      isOrderError ||
      !isTaskAlreadyStarted ||
      isUploadingScreenshot ||
      isToggleTaskStateLoading,
    [
      currentIsHasL1L2Error,
      isClickedEndMassage,
      isDisconnectMachineArm,
      isOrderError,
      isSocketConnected,
      isStartBtnDisabled,
      isTaskAllCompleted,
      isTaskAlreadyStarted,
      isToggleTaskStateLoading,
      isUploadingScreenshot,
      massageState,
      steps?.length,
    ]
  );

  // 痛点治疗且当前正在按摩时显示痛点治疗相关UI
  const isShowIntensifyUIState = useMemo(
    () => intensifyTreatmentStatus?.is_intensifying && massageState === 'Running_Massaging',
    [intensifyTreatmentStatus?.is_intensifying, massageState]
  );

  // 监听重新返回页面事件
  useUpdateEffect(() => {
    if (documentVisibility === 'visible') {
      Toast.clear();
      socketInstance.current?.socket?.connect();
    }
    if (documentVisibility === 'hidden') {
      socketInstance.current?.socket?.close();
    }
  }, [documentVisibility]);

  // 获取机械臂碰撞检测状态
  const handleGetCollisionCheckState = useCallback(async () => {
    const data = await socketInstance.current.getCollisionCheckState();
    const checkState = data?.state as Massage.CheckMoxibustionState;
    const executorType = data?.executorType as Massage.ExecutorType;
    checkArmRef.current?.setExecutorType(executorType);
    checkArmRef.current?.setIsShowClose?.(data?.error === 'Visual');
    // 只埋点记录订单结束时
    checkArmRef.current?.setMassageProcess(
      executorType === 'SuspensionMoxibustion' ? 'remove-moxibustion-head' : 'remove-massage-head'
    );

    if (checkState === 'Activated') {
      checkArmRef.current?.show();
    }
    if (checkState === 'Inactive') {
      checkArmRef.current?.hide();
    }
    if (checkState === 'Activated_Passed') {
      checkArmRef.current?.show();
      checkArmRef.current?.setBtnEnabled();
    }
    if (checkState === 'Activated_Denied') {
      checkArmRef.current?.show();
      checkArmRef.current?.setBtnDisabled();
    }
  }, [socketInstance]);

  // 获取剩余时间
  const getTaskProgressAndRemain = useCallback(async () => {
    const data = await socketInstance.current?.getTaskProgress();
    setProgressInfo(data);
    // setCurrentProgress(data?.progress);
    // setRemainTimes(data?.remaining);
  }, [socketInstance]);

  // 获取结节数据
  const getBodyNodeAndLineList = useCallback(async () => {
    try {
      const data = await socketInstance.current.getPalpationData();

      const formatData = convertObjectKeysToCamelCase(data);

      const leftNodeList = formatData?.palpationData?.noduleSet?.left?.map(item => ({
        isLeft: true,
        id: item.noduleId,
        noduleId: item.noduleId,
        name: item.index.toString(),
        position: item.position,
        pixelPosition: item.pixelPosition,
      }));
      const rightNodeList = formatData?.palpationData?.noduleSet?.right?.map(item => ({
        isLeft: false,
        id: item.noduleId,
        noduleId: item.noduleId,
        name: item.index.toString(),
        position: item.position,
        pixelPosition: item.pixelPosition,
      }));
      setBodyNodeList([...(leftNodeList || []), ...(rightNodeList || [])]);

      // 自定义画线流程渲染模型自定义走线
      if (latestOrder?.current?.modelConfig?.[0]?.value === 'custom-draw-line') {
        const data = await socketInstance.current.getActions();

        setCustomLineDrawInfo(
          formatData?.palpationData?.customRoutes?.map(item => ({
            lineId: item.id,
            state: data?.actions?.find(item2 => (item2?.target as any)?.route_id === item.id)?.state as any,
            coord: item.techModelCoordinates?.flat(),
          }))
        );
      }
    } catch (e) {
      console.error(e);
    }
  }, [latestOrder, socketInstance]);

  // 回显参数
  const reshowParamsData = useCallback(
    async (steps: GetMassageTaskInfoResponse[], currentStep: number) => {
      let controlPanelData = getControlPanelData();
      if (!controlPanelData?.length) {
        const { data } = await service.massage.getMassageControlPannel(currentServingOrder.orderId);
        controlPanelData = data;
      }

      const currentControlItems = controlPanelData[currentStep]?.controlItemConfigs;
      try {
        getTaskProgressAndRemain();
        const currentItem = steps[currentStep]?.massageItem;
        const machineStateInfo = await socketInstance.current.getMachineState();
        setBgmState(machineStateInfo?.background_music);

        const latestValue: Record<Massage.ControlItemOptions, number | [number, number]> = {} as any;

        latestValue['force_range'] = machineStateInfo?.variable_force_range;
        // 回显音量
        const volume = await socketInstance.current.getVolume();
        latestValue['robot.voice'] = volume?.volume || 0;

        const stepInfo = await socketInstance.current.getMassageAction();
        setActionSteps(stepInfo?.values);
        latestValue['robot.step'] = stepInfo?.current?.value;

        // 动态渲染矩阵式艾灸区域
        try {
          if (currentControlItems?.some(item => item.name === 'moxibustion.tech')) {
            const moxibustionCompletedAreas = await socketInstance.current.getMoxibustionCompletedAreas();
            setStoreMoxibustionCompletedAreas(moxibustionCompletedAreas.moxibustion_areas);
          }
        } catch (e) {
          console.log('获取矩阵式艾灸进度失败', e);
        }

        // 回显高度
        latestValue['robot.height'] = machineStateInfo.height;
        // 回显结节设置数量
        latestValue['shockwave.nodule_hits'] = machineStateInfo.shockwave_nodule_hits;
        // 回显速度
        latestValue['robot.velocity'] = machineStateInfo?.velocity || 0;
        // 回显力度
        latestValue['robot.force'] = machineStateInfo?.force || 0;

        if (currentItem === 'Shockwave') {
          const shockwavePower = machineStateInfo?.shockwave?.power;
          latestValue['shockwave.power'] = isNaN(shockwavePower) ? 0 : Math.floor(shockwavePower) || 0;
          latestValue['shockwave.frequency'] = machineStateInfo?.shockwave?.frequency || 0;
          previousShockwaveFrequency.current = machineStateInfo?.shockwave?.frequency || 0;
        }
        if (currentItem === 'RadioFrequency') {
          const radioPower = machineStateInfo?.radio_frequency?.power;
          latestValue['radio_frequency.power'] = isNaN(radioPower) ? 0 : Math.round((radioPower * 10000) / 100) || 0;
        }
        if (
          currentItem === 'HotStone' &&
          isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.8.10')
        ) {
          latestValue['hot_stone.power'] = machineStateInfo?.hotstone?.power
            ? +new Decimal(machineStateInfo?.hotstone?.power).mul(100).toFixed(0)
            : 0;
          latestValue['hot_stone.temperature_level'] = machineStateInfo?.hotstone?.temperature_level;
          latestValue['hot_stone.mode'] = machineStateInfo?.hotstone?.mode;
        }

        // 回显按摩/艾灸手法
        try {
          if (currentControlItems?.some(item => ['massage.tech', 'moxibustion.tech'].includes(item.name))) {
            const techInfo = await socketInstance.current.getMassageTech();
            if (currentItem === 'Massage') {
              latestValue['massage.tech'] = techInfo?.current?.value;
            }
            if (currentItem === 'Moxibustion') {
              latestValue['moxibustion.tech'] = techInfo?.current?.value;
            }
            setMassageTechValues(techInfo?.values);
          }
        } catch (e) {
          console.log('回显手法失败', e);
        }

        setAllValues(prev => ({ ...prev, ...latestValue }));
      } catch (e) {
        console.log('回显设置参数失败:', e);
      }
    },
    [currentServingOrder, getControlPanelData, getTaskProgressAndRemain, setAllValues, socketInstance]
  );

  /** 回显当前按摩任务数据 */
  const reshowDataFromCurrentTask = useCallback(
    async (task: GetMachineStateResponse, steps: GetMassageTaskInfoResponse[]) => {
      const taskSequence = await socketInstance.current.getTaskSequenceStatus();
      setTaskSequence(taskSequence);
      // 判断当前任务是否已开始
      if (taskSequence?.task_sequence[0]?.state !== 'Created') {
        setIsTaskAlreadyStarted(true);
      }

      const currentStep = taskSequence?.task_sequence?.findIndex(item => item.state !== 'Success');

      setCurrentStep(currentStep);
      setMassageState(task.state);
      task.state === 'Running_Massaging' && setCurrentProcess('Massage');

      // 兼容低于1.6.10的版本
      if (
        task?.error_state?.some(item => exposedAreaCode?.includes(item)) &&
        !isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.6.10')
      ) {
        setCurrentExposedAreaCode(task?.error_state?.[0]);
        physicalTherapyDrawerRef.current?.show();
      }

      if (task?.error_state?.length) {
        if (task?.error_state?.some(item => ['ARM_RECONNECTING', 'ARM_ERROR_RECOVERING'].includes(item))) {
          setIsArmReconnecting(true);
        }

        const errCode = task?.error_state?.[0];

        if (errCode === 'ARM_RECONNECTING') {
          // 机械臂断联不处理人工单
          if (steps[currentStep]?.massageType !== 'ManualMassage') {
            setTimeout(() => robotExceptionPromptRef.current?.hanldeRobotExceptionEvents(errCode), 2000);
          }
        } else {
          setTimeout(() => robotExceptionPromptRef.current?.hanldeRobotExceptionEvents(errCode), 2000);
        }
      }

      reshowParamsData(steps, currentStep);
    },
    [
      currentServingOrder?.device?.softwareVersion,
      reshowParamsData,
      setCurrentProcess,
      setCurrentStep,
      setMassageState,
      socketInstance,
    ]
  );

  // 更新任务状态
  const updateTaskSequenceStatus = useCallback(async () => {
    const taskSequence = await socketInstance.current.getTaskSequenceStatus();
    setTaskSequence(taskSequence);
    // 判断当前任务是否已开始
    if (taskSequence?.task_sequence[0]?.state !== 'Created') {
      setIsTaskAlreadyStarted(true);
    }
  }, [socketInstance]);

  // 获取按摩任务相关信息
  const getMassageTask = useCallback(
    async orderId => {
      try {
        // 判断当前是否有订单
        const { data: currentOrder } = await service.service.getServingOrderByOperator();

        dispatch(setCurrentServingOrder(currentOrder || ({} as any)));

        if (!currentOrder) {
          const previousOrderId = lookie.get(CURRENT_ORDER_ID);
          if (previousOrderId) {
            const { data } = await service.service.getServiceOrder(previousOrderId);

            nav(data?.state === 'Completed' ? ALL_PAGES.HOME : ALL_PAGES.MEMBER_DETAIL, {
              state: {
                orderId: previousOrderId,
                isNeedShowDrawerAfterFinish: true,
                customerId: latestOrder?.current?.customerId,
              },
            });
          } else {
            nav(ALL_PAGES.MEMBER_DETAIL, {
              state: {
                orderId: previousOrderId,
                isNeedShowDrawerAfterFinish: true,
              },
            });
          }
          return;
        }

        const _currentProcess = await socketInstance.current.getCurrentStep();

        if (currentOrder.step !== 'RoboticTask') {
          handleNavFromOrderInfo(currentOrder, nav);
          return;
        }

        const { data: taskInfo } = await service.massage.getMassageTaskInfo(orderId);

        setLineHits(taskInfo?.find(item => item.massageType === 'Massage')?.taskParam?.shockwave?.lineHits);

        setSteps(taskInfo);
        const data = await socketInstance.current.getMachineState();
        setBgmState(data?.background_music);

        // 判断当前是否有L0-L2 error
        if (
          data?.error_state?.some(errCode => ['l0', 'l1', 'l2'].includes(RobotExceptionEventsHandler[errCode]?.level))
        ) {
          setCurrentIsHasL1L2Error(true);
        }

        setIntensifyTreatmentStatus(data?.intensify_treatment_status || ({} as any));

        if (
          _currentProcess?.step === 'Massage' &&
          [
            'Preparing_HumanDetecting',
            'Preparing_AcupointsAnalyzing',
            'Preparing_KnotsMarking',
            'Preparing_HumanDetectingTimeout',
            'Preparing_AcupointsCalibration',
          ].includes(data.state)
        ) {
          setCurrentProcess('VisualCheck');
        } else if (
          _currentProcess?.step === 'Massage' &&
          [
            'Preparing_WaitingInstallation',
            'Preparing_InstallingWiring',
            'Preparing_MovingToMatrixInstallation',
          ].includes(data.state)
        ) {
          setCurrentProcess('Installation');
        } else {
          setCurrentProcess(_currentProcess?.step);
        }

        reshowDataFromCurrentTask(data, taskInfo);

        handleGetCollisionCheckState();

        if (isNewSoftwareVersion(currentOrder?.device?.softwareVersion, '1.6.5')) {
          try {
            const dynamicActions = await socketInstance.current.getActions();
            setDynamicActions(convertObjectKeysToCamelCase(dynamicActions?.actions));

            // 自定义画线坐标数据渲染
            if (currentOrder?.modelConfig?.[0]?.value === 'custom-draw-line') {
              const palpationData = await socketInstance.current.getPalpationData();
              setCustomLineDrawInfo(
                palpationData?.palpation_data?.custom_routes?.map(item => ({
                  lineId: item.id,
                  state: dynamicActions?.actions?.find(item2 => (item2?.target as any)?.route_id === item.id)
                    ?.state as any,
                  coord: item.tech_model_coordinates?.flat(),
                }))
              );
            }
          } catch (e) {
            console.error(e);
          }
        }

        const hitTargetInfo = await socketInstance.current.getHitTargetStates();
        setStoredHitTargetInfo(hitTargetInfo);

        setTimeout(() => {
          heatTipRef.current?.handleTemperatureChange(data?.temperature_state);
        }, 2500);

        setIsStartBtnDisabled(false);
      } catch (e) {
        console.log('error', e);
      }
    },
    [
      dispatch,
      handleGetCollisionCheckState,
      latestOrder,
      nav,
      reshowDataFromCurrentTask,
      setCurrentProcess,
      setDynamicActions,
      setSteps,
      socketInstance,
    ]
  );

  // 动态渲染矩阵式艾灸区域
  const renderMoxibustionAreas = useCallback(
    (areas: GetMoxibustionCompletedAreasResponse['moxibustion_areas']) => {
      if (!currentServingOrder?.productItems.includes('Moxibustion')) {
        return;
      }

      const renderList: MatrixMoxibustionArea[] = [];

      for (const item of areas) {
        renderList.push({
          name: `image${item.area}`,
          opacity: item?.pending ? 0.4 : 1,
        });
      }

      model2dRef.current?.setMatrixMoxibustionArea(renderList, {
        isAbdomen: areas.some(item => [6, 7].includes(item.area)),
      });
    },
    [currentServingOrder]
  );

  const navPage = useCallback(
    (currentIsEndFromUser: boolean) => {
      // 1.9.10 版本新增跳转逻辑 磁震波&&射频 强制用户进入动作复测页面
      if (
        latestOrder?.current?.productItems?.includes('Shockwave') ||
        latestOrder?.current?.productItems?.includes('RadioFrequency')
      ) {
        const productItems = latestOrder?.current?.productItems;
        nav(productItems?.includes('Shockwave') ? ALL_PAGES.ACTION_RETEST : ALL_PAGES.BODY_REMEASUREMENT, {
          state: {
            isAllProcessSuccess: !currentIsEndFromUser,
            orderId: currentOrderIdRef.current,
            isNeedShowDrawerAfterFinish: true,
          },
        });
      } else {
        nav(currentIsEndFromUser ? ALL_PAGES.MEMBER_DETAIL : ALL_PAGES.HOME, {
          state: {
            orderId: currentOrderIdRef.current,
            isNeedShowDrawerAfterFinish: true,
            customerId: latestOrder?.current?.customerId,
          },
        });
      }
    },
    [currentOrderIdRef, latestOrder, nav]
  );

  // 上传模型截图
  const handleUploadScreenshot = useCallback(async () => {
    if (!currentOrderId || getCurrentProcess() !== 'Massage') {
      return;
    }

    const base64String = model2dRef.current?.screenshot();

    if (!base64String) {
      console.error('截图失败');
      return;
    }
    const url = await uploadBase64Img(base64String);
    try {
      await service.massage.save3DScreenshot({
        serviceOrderId: currentOrderIdRef.current,
        screenshotUrl: url,
      });
    } catch (e) {
      console.error(e);
    }
  }, [currentOrderId, currentOrderIdRef, getCurrentProcess]);

  const onRobotEvent: onRoboticEventListener = useCallback(
    event => {
      if (isUnmount.current) return;

      console.log('[onRobotEvent]:', event);
      //数据埋点记录
      if (event.name === 'progress_change') {
        apiCounters++;
        //避免频繁记录
        if (apiCounters % 10 === 0) {
          recordLog('robot-event-log', event);
        }
      } else {
        recordLog('robot-event-log', event);
      }
      switch (event.name) {
        case 'state_change':
          const state = event.details?.state;
          PalpationEventBus.emit('state_change', event);

          setMassageState(state);
          setIsTaskAlreadyStarted(true);
          if (['Preparing_WaitingInstallation', 'Preparing_MovingToMatrixInstallation'].includes(state)) {
            setCurrentProcess('Installation');
          } else if (
            [
              'Preparing_HumanDetecting',
              'Preparing_AcupointsAnalyzing',
              'Preparing_KnotsMarking',
              'Preparing_HumanDetectingTimeout',
              'Preparing_AcupointsCalibration',
            ].includes(state)
          ) {
            setCurrentProcess('VisualCheck');
            physicalTherapyDrawerRef.current?.hide();
          } else if (
            [
              'Preparing_WaitingDrawCustomRoute',
              'Preparing_DrawCustomRoute',
              'Preparing_WaitingTechRouteGenerating',
              'Preparing_Setting',
            ].includes(state)
          ) {
            setCurrentProcess('CustomPalpation');
          } else if (['Running_Massaging', 'Running'].includes(state)) {
            setControlFooterContentShow(null);
            setCurrentProcess('Massage');
            reshowParamsData(getSteps(), getCurrentStep());
          } else if (state === 'Paused') {
            setControlFooterContentShow(null);
            robotExceptionPromptRef.current?.setIsConfirmBtnDisabled(prev => ({
              ...prev,
              FORCE_CONTROL_PROTECT: false,
            }));
          }
          break;
        case 'hotstone_change':
          if (isStepperValueChanging.current) {
            console.log('已忽略本次热砭石参数改变！！！');
            return;
          }

          const hotStonePower = event.details.power;
          setAllValues(prevValue => ({
            ...prevValue,
            'hot_stone.mode': event.details?.mode,
            'hot_stone.power': hotStonePower ? +new Decimal(hotStonePower).mul(100).toFixed(0) : 0,
            'hot_stone.temperature_level': event.details?.temperature_level,
          }));
          break;
        case 'step_change':
          PalpationEventBus.emit('step_change', event);

          const step = event.details.step;
          if (['VisualCheckAndPalpation', 'VisualCheck'].includes(step)) {
            setCurrentProcess('VisualCheckAndPalpation');
          }
          break;

        case 'task_finished':
          if (isIgnoreTaskFinished.current) return;

          const isFinished = getCurrentStep() === getSteps()?.length - 1;
          // 全部任务是否结束
          if (!isFinished) {
            setIsUploadingScreenshot(true);
            handleUploadScreenshot()
              .catch(() => {})
              .finally(() => {
                setCurrentStep(getCurrentStep() + 1);
                reshowParamsData(getSteps(), getCurrentStep() + 1);
                setIsUploadingScreenshot(false);
                updateTaskSequenceStatus();
              });
          }
          break;
        // 自动结束事件
        case 'task_sequence_result':
          if (isIgnoreTaskFinished.current) return;

          setIsTaskAllCompleted(true);
          const isSuccess = event.details.success;
          const finishedReason = event.details?.results?.finishedReason;

          let params: QuitMassageFromEventParams = {
            serviceOrderId: currentOrderIdRef.current,
            success: isSuccess,
          };
          // 是否是用户手动结束订单
          let isEndFromUser = false;

          if (isNewSoftwareVersion(latestOrder.current?.device?.softwareVersion, '1.7.0')) {
            isEndFromUser = finishedReason === 'User';
            if (latestOrder.current?.productItems?.includes('Shockwave')) {
              params.shockwaveProbeCount = event.details?.results?.shockwaveProbeCount;
            }
          }

          /**
           * 是否是用户手动结束订单
           * 1.7.0以前的版本`isEndFromUser`一直为false,需要通过`isClickedEnd`判断
           */
          const currentIsEndFromUser = isClickedEnd.current || isEndFromUser;

          service.massage.quitMassageFromEvent(params).then(async response => {
            try {
              await handleUploadScreenshot();
            } catch (e) {
              console.error(e);
            }

            if (window.location.pathname !== '/service/massage-control') {
              return;
            }

            if (isSuccess) {
              if (latestOrder.current?.productItems?.includes('Shockwave')) {
                // 正常结束并且有剩余次数 弹保养提示的弹窗
                if (
                  (!!response.data?.probeMaxUseCount || !!response.data?.orderMaxUseCount) &&
                  getCurrentProcess() === 'Massage'
                ) {
                  Dialog.alert({
                    title: '磁震波设备保养提示',
                    content:
                      latestOrder.current?.shockwaveType === 1 ? (
                        <div>
                          <span>磁震波每击打{response.data?.probeMaxUseCount / 10000}万次需要保养，</span>
                          <br />
                          <span>
                            当前设备已击打
                            <span style={{ color: '#05f' }}>{event.details?.results?.shockwaveProbeCount}</span>次,
                          </span>
                          <br />
                          <span>若不处理会影响顾客的理疗体验，</span>
                          <br />
                          <span>建议您及时保养。有任何问题可以联系客服。</span>
                        </div>
                      ) : (
                        <div>
                          <span>当前设备已达到较高打击次数</span>
                          <br />
                          <span>为了不影响顾客的理疗体验，建议您及时保养， </span>
                          <br />
                          <span>有任何问题可以联系客服。</span>
                        </div>
                      ),
                    confirmText: '我知道了',
                    onConfirm: async () => {
                      // 强制用户作动作复测
                      nav(ALL_PAGES.ACTION_RETEST, {
                        state: {
                          isAllProcessSuccess: !currentIsEndFromUser,
                          orderId: currentOrderIdRef.current,
                          isNeedShowDrawerAfterFinish: true,
                        },
                      });
                    },
                    className: cx('custom-dialog'),
                  });
                } else {
                  navPage(currentIsEndFromUser);
                }
              } else {
                navPage(currentIsEndFromUser);
              }
            } else {
              setIsOrderError(true);
              if (latestOrder.current?.productItems?.includes('Shockwave')) {
                // 强制用户进入动作测试页面
                robotExceptionPromptRef.current?.showOrderErrorPrompt({
                  navUrl: ALL_PAGES.ACTION_RETEST,
                  navOptions: {
                    state: {
                      isAllProcessSuccess: false,
                      orderId: currentOrderIdRef.current,
                      isNeedShowDrawerAfterFinish: false,
                    },
                  },
                  orderId: currentOrderIdRef.current,
                  shockwaveProbeCount: event.details?.results?.shockwaveProbeCount,
                  finishedReason,
                });
              } else if (latestOrder.current?.productItems?.includes('RadioFrequency')) {
                // 强制用户进入围度测量页面
                robotExceptionPromptRef.current?.showOrderErrorPrompt({
                  navUrl: ALL_PAGES.BODY_REMEASUREMENT,
                  navOptions: {
                    state: {
                      isAllProcessSuccess: false,
                      orderId: currentOrderIdRef.current,
                      isNeedShowDrawerAfterFinish: false,
                    },
                  },
                  orderId: currentOrderIdRef.current,
                  finishedReason,
                });
              } else {
                robotExceptionPromptRef.current?.showOrderErrorPrompt({
                  navUrl: ALL_PAGES.MEMBER_DETAIL,
                  orderId: currentOrderIdRef.current,
                  finishedReason,
                });
              }
            }
          });

          break;

        case 'hit_target_change':
          socketInstance.current
            .getHitTargetStates()
            .then(hitTargetInfo => {
              setStoredHitTargetInfo(hitTargetInfo);
            })
            .catch(e => {
              console.error('socket error [getHitTargetStates]', e);
            });
          break;
        case 'error_fire':
          const errorOccurName = event.details.error;
          //兼容1.6.0-1.6.5人体检测异常,当error_image不存在时说明是以前版本的异常处理
          const isNeedCompatibilityForDetect = !event.details?.error_image;

          if (['ARM_RECONNECTING', 'ARM_ERROR_RECOVERING'].includes(errorOccurName)) {
            setIsArmReconnecting(true);
          }

          if (isNeedCompatibilityForDetect) {
            if (exposedAreaCode?.includes(errorOccurName)) {
              setCurrentExposedAreaCode(errorOccurName);
              physicalTherapyDrawerRef.current?.show();
              return;
            }
            //除人体检测异常外的处理逻辑
            robotExceptionPromptRef.current?.hanldeRobotExceptionEvents(errorOccurName);
          } else {
            // 人体检测异常时的处理逻辑
            PalpationEventBus.emit('error_fire', event);

            //无需兼容的情况下，则list中的这些人体检测异常不需要处理
            ![...needCompatibilityCode, ...exposedAreaCode].includes(errorOccurName) &&
              robotExceptionPromptRef.current?.hanldeRobotExceptionEvents(errorOccurName);
          }

          break;
        case 'error_done':
          const errorRecoverName = event.details.error;
          if (['ARM_RECONNECTING', 'ARM_ERROR_RECOVERING'].includes(errorRecoverName)) {
            setIsArmReconnecting(false);
          }
          // 人体检测异常时触发error_done则关闭所有错误提示
          PalpationEventBus.emit('error_done', event);
          robotExceptionPromptRef.current?.hanldeRobotExceptionRecoverEvents(errorRecoverName);
          break;
        /** 进度变化 */
        case 'progress_change':
          setProgressInfo(event.details);
          break;
        /** 力度变化 */
        case 'force_change':
          Toast.clear();

          if (isStepperValueChanging.current) {
            console.log('已忽略本次按摩头力度改变！！！');
            return;
          }

          const force = event.details.force;
          if (force === 0) return;

          setAllValues(prevValue => ({ ...prevValue, 'robot.force': force }));
          break;
        case 'shockwave_states_change':
          Toast.clear();

          try {
            const formatParams = event.details;
            const newParams: Record<Massage.ControlItemOptions, number> = {} as any;

            // 不处理传过来的值为0的情况
            if (formatParams?.power && +formatParams?.power > 0) {
              newParams['shockwave.power'] = isNaN(Math.floor(formatParams.power)) ? 0 : Math.floor(formatParams.power);
            }

            if (formatParams?.frequency && +formatParams?.frequency > 0) {
              // 只处理频率改变的情况
              if (
                previousShockwaveFrequency.current !== formatParams.frequency &&
                formatParams.frequency >= 12 &&
                latestOrder.current?.shockwaveType === ShockwaveType['DEJI']
              ) {
                Toast.show('超过12Hz理疗渗透力会降低哦');
              }
              newParams['shockwave.frequency'] = formatParams.frequency;
            }
            previousShockwaveFrequency.current = formatParams?.frequency;

            if (isStepperValueChanging.current) {
              console.log('已忽略本次冲击波参数改变！！！');
              return;
            }

            setAllValues(prev => ({ ...prev, ...newParams }));
          } catch (e) {
            console.log(e);
          }
          break;
        case 'radio_frequency_states_change':
          Toast.clear();

          if (isStepperValueChanging.current) {
            console.log('已忽略本次射频参数改变！！！');
            return;
          }

          const powerValue = event.details?.power;

          setAllValues(prev => ({
            ...prev,
            'radio_frequency.power': isNaN(Math.round((powerValue * 10000) / 100))
              ? 0
              : Math.round((powerValue * 10000) / 100),
          }));
          break;
        case 'moxibustion_height_alarm':
        case 'height_minimum_protected':
          Toast.show('检测到突出部位，为避免接触，已自动调整艾灸高度。离开突出部位后将自动降低高度。', {
            duration: 5000,
          });
          break;
        case 'ignore_pausing':
          Toast.show('当前服务时长不足，将自动结束当前服务。');
          break;
        case 'microphone_change':
          funcAdjusterRef.current?.setMicOpenState(event?.details?.enable ? 'on' : 'off');
          break;
        case 'broadcast_change':
          funcAdjusterRef.current?.setBroadcastState(event?.details?.enable ? 'on' : 'off');
          break;
        case 'background_music_change':
          funcAdjusterRef.current?.setBgmState(event?.details);
          break;
        case 'volume_change':
        case 'height_change':
        case 'massage_tech_change':
        case 'action_change':
        case 'velocity_change':
          const backupAllValues: Record<Massage.ControlItemOptions, number> = {} as any;

          if (event.name === 'volume_change') {
            backupAllValues['robot.voice'] = event.details?.volume;
          }
          if (event.name === 'height_change') {
            backupAllValues['robot.height'] = event.details?.height;
          }
          if (event.name === 'massage_tech_change') {
            backupAllValues['massage.tech'] = event.details?.current?.value;
            backupAllValues['moxibustion.tech'] = event.details?.current?.value;
          }
          if (event.name === 'action_change') {
            const currentActionId = event?.details?.actionId;
            backupAllValues['robot.step'] = currentActionId;

            // 动态绘制走线
            if (latestOrder.current.modelConfig?.[0]?.key === 'Shockwave' && getCurrentProcess() === 'Massage') {
              const handleDraw = () => {
                if (!getDynamicActions()?.length) {
                  socketInstance.current.getActions().then(data => {
                    model2dRef.current?.activeCustomLine(
                      (data?.actions?.find(item => item.actionId === event?.details?.actionId)?.target as any)
                        ?.routeId || event?.details?.actionId
                    );
                  });
                } else {
                  model2dRef.current?.activeCustomLine(
                    (getDynamicActions()?.find(item => item.actionId === event?.details?.actionId)?.target as any)
                      ?.routeId || event?.details?.actionId
                  );
                }
              };

              if (!getIsRenderModelOKWithoutTimeout()) {
                setTimeout(() => handleDraw(), 1500);
              } else {
                handleDraw();
              }
            }

            if (isNewSoftwareVersion(latestOrder.current?.device?.softwareVersion, '1.6.5')) {
              setDynamicActions(prevVal =>
                prevVal.map(item => ({
                  ...item,
                  state:
                    item.actionId === currentActionId
                      ? 'RUNNING'
                      : item.actionId < currentActionId
                      ? 'DONE'
                      : 'PENDING',
                }))
              );
            }

            if (isNewSoftwareVersion(latestOrder.current?.device?.softwareVersion, '1.8.0')) {
              const force = event.details.force;
              const forceRange = event.details.variableForceRange;
              if (force && force !== 0) {
                setAllValues(prev => ({ ...prev, 'robot.force': force }));
              }

              if (forceRange?.length) {
                setAllValues(prev => ({ ...prev, force_range: forceRange }));
              }
            }
          }
          if (event.name === 'velocity_change') {
            backupAllValues['robot.velocity'] = event?.details?.velocity;
          }
          setAllValues(prev => ({ ...prev, ...backupAllValues }));
          break;
        case 'temperature_change':
          heatTipRef.current.handleTemperatureChange(event.details);
          break;
        /** 矩阵式艾灸 or 颈部按摩头 检测 */
        case 'moxibustion_check_state_change':
          const checkState = event.details.state;
          /** 判断是 矩阵式艾灸 or 颈部按摩头*/
          const executorType = event?.details?.executorType;
          checkArmRef.current?.setExecutorType(executorType);
          checkArmRef.current?.setIsShowClose?.(event?.details?.error === 'Visual');
          checkArmRef.current?.setMassageProcess(
            executorType === 'SuspensionMoxibustion' ? 'remove-moxibustion-head' : 'remove-massage-head'
          );

          if (checkState === 'Activated') {
            checkArmRef.current?.show();
          }
          if (checkState === 'Inactive') {
            checkArmRef.current?.hide();
          }
          if (checkState === 'Activated_Passed') {
            checkArmRef.current?.show();
            checkArmRef.current?.setBtnEnabled();
          }
          if (checkState === 'Activated_Denied') {
            checkArmRef.current?.show();
            checkArmRef.current?.setBtnDisabled();
          }
          break;
        case 'palpation_data':
          PalpationEventBus.emit('palpation_data', event);
          break;
        case 'palpation_image':
          PalpationEventBus.emit('palpation_image', event);
          break;
        case 'knots_limited_warning':
          PalpationEventBus.emit('knots_limited_warning');
          break;
        case 'moxibustion_areas_change':
          setStoreMoxibustionCompletedAreas(event.details?.moxibustion_areas);
          break;
        case 'variable_force_range_change':
          setAllValues(prev => ({ ...prev, force_range: event.details?.variable_force_range }));
          break;
        case 'intensify_treatment_status_change':
          setIntensifyTreatmentStatus(event.details);
          break;
        case 'custom_route_drawing_start':
        case 'custom_route_detected':
        case 'custom_route_max_quantity_exceeded':
        case 'custom_route_detected_multiple_hands':
        case 'custom_route_detect_timeout':
        case 'custom_route_generated':
        case 'acupoint_calibration_repeated_marking':
        case 'acupoint_calibration_spine_point_detected':
          PalpationEventBus.emit(event.name, event);
          break;
      }
    },
    [
      currentOrderIdRef,
      getCurrentProcess,
      getCurrentStep,
      getDynamicActions,
      getIsRenderModelOKWithoutTimeout,
      getSteps,
      handleUploadScreenshot,
      isUnmount,
      latestOrder,
      nav,
      navPage,
      recordLog,
      reshowParamsData,
      setAllValues,
      setCurrentProcess,
      setCurrentStep,
      setDynamicActions,
      setMassageState,
      socketInstance,
      updateTaskSequenceStatus,
    ]
  );

  // 当手动调节参数时，一定时间内忽略机器人事件值改变事件
  const handleOnStepperValueChange = useCallback(() => {
    if (isStepperValueChanging.current) {
      clearTimeout(isStepperValueChangingTimer.current);
    }
    isStepperValueChanging.current = true;
    isStepperValueChangingTimer.current = setTimeout(() => (isStepperValueChanging.current = false), 500);
  }, []);

  /** 获取当前按摩师正在进行的服务订单-init */
  const initCurrentTaskAndSocket = useCallback(
    async (cb?: VoidFunc) => {
      const { data: task } = await service.service.getServingOrderByOperator();
      dispatch(setCurrentServingOrder(task || ({} as any)));

      if (!task?.orderId) {
        const previousOrderId = lookie.get(CURRENT_ORDER_ID);
        if (previousOrderId) {
          const { data } = await service.service.getServiceOrder(previousOrderId);

          nav(data?.state === 'Completed' ? ALL_PAGES.HOME : ALL_PAGES.MEMBER_DETAIL, {
            orderId: previousOrderId,
            isNeedShowDrawerAfterFinish: true,
            customerId: latestOrder?.current?.customerId,
          });
        } else {
          nav(ALL_PAGES.MEMBER_DETAIL);
        }
        return;
      }

      const { data: productInfo } = await service.service.getProduct(task?.storeGoodsId);
      setProductInfo(productInfo);

      lookie.set(CURRENT_ORDER_ID, task?.orderId);

      // 获取控制面板参数
      const { data: panelData } = await service.massage.getMassageControlPannel(task?.orderId);

      setControlPanelData(convertObjectKeysToCamelCase(panelData));

      const { data: connectInfo } = await service.device.getConnectionInfo(task?.deviceId);
      initSocket(connectInfo);

      socketInstance.current.onConnect(() => {
        Toast.clear();
        robotExceptionPromptRef.current?.hanldeRobotExceptionRecoverEvents?.('LOSE_CONNECTION_L3');
        clearTimeout(palpationTimeoutTimer.current);
        setIsSocketConnected(true);
      });

      socketInstance.current.onDisconnect(reason => {
        Toast.clear();
        setIsSocketConnected(false);

        if (reason !== 'io client disconnect') {
          // 当前是否在手诊中
          const currentStepIsVisualCheck = ['VisualCheck', 'VisualCheckAndPalpation'].includes(getCurrentProcess());

          robotExceptionPromptRef.current?.hanldeRobotExceptionEvents(
            currentStepIsVisualCheck ? 'LOSE_CONNECTION_L3' : 'LOSE_CONNECTION_L4'
          );

          if (currentStepIsVisualCheck && documentVisibilityRef.current === 'visible') {
            palpationTimeoutTimer.current = setTimeout(() => {
              robotExceptionPromptRef.current?.hanldeRobotExceptionEvents('NETWORK_BROKEN');
            }, 30 * 1000);
          }
        }
      });

      // 注册socketio s2c事件
      socketInstance.current.onRobotEvent(onRobotEvent);

      if (task?.orderId) {
        lookie.set(SELECTED_CUSTOMER_ID, task?.customerId);
      }

      // 获取手诊数据
      if (task?.productItems?.some(item => item === 'Shockwave' || item === 'Massage')) {
        getBodyNodeAndLineList();
      }
      cb?.();
    },
    [
      dispatch,
      documentVisibilityRef,
      getBodyNodeAndLineList,
      getCurrentProcess,
      initSocket,
      latestOrder,
      nav,
      onRobotEvent,
      setControlPanelData,
      socketInstance,
    ]
  );

  const endService = useLockFn(
    useCallback(async () => {
      if (isDisconnectMachineArm || isTaskAllCompleted || isClickedEndMassage) return;

      return await Dialog.confirm({
        title: '确认要结束服务吗?',
        content: '确认后将直接结束服务',
        confirmText: '确认结束',
        onConfirm() {
          isIgnoreTaskFinished.current = true;
          isClickedEnd.current = true;

          setIsStoping(true);
          return new Promise(resolve => {
            service.massage
              .quitMassage({
                terminate: false,
                serviceOrderId: currentOrderIdRef.current,
              })
              .then(async response => {
                setIsClickedEndMassage(true);

                try {
                  const { data } = response || {};

                  if (
                    latestOrder.current?.productItems?.includes('Shockwave') &&
                    getCurrentProcess() === 'Massage' &&
                    (!!data?.probeMaxUseCount || !!data?.orderMaxUseCount)
                  ) {
                    Dialog.alert({
                      title: '磁震波设备保养提示',
                      content:
                        latestOrder.current?.shockwaveType === 1 ? (
                          <div>
                            <span>磁震波每击打{data?.probeMaxUseCount / 10000}万次需要保养，</span>
                            <br />
                            <span>
                              当前设备已击打
                              <span style={{ color: '#05f' }}>{data?.shockwaveProbeCount}</span>次,
                            </span>
                            <br />
                            <span>若不处理会影响顾客的理疗体验，</span>
                            <br />
                            <span>建议您及时保养。有任何问题可以联系客服。</span>
                          </div>
                        ) : (
                          <div>
                            <span>当前设备已达到较高打击次数</span>
                            <br />
                            <span>为了不影响顾客的理疗体验，建议您及时保养， </span>
                            <br />
                            <span>有任何问题可以联系客服。</span>
                          </div>
                        ),
                      confirmText: '我知道了',
                      onConfirm: async () => {
                        if (!data.waitTaskFinishedEvent) {
                          if (getCurrentStep() === 0) {
                            try {
                              await handleUploadScreenshot();
                            } catch (e) {
                              console.log('上传图片error', e);
                            }
                          }

                          nav(ALL_PAGES.ACTION_RETEST, {
                            state: {
                              isAllProcessSuccess: false,
                              orderId: currentOrderIdRef.current,
                              isNeedShowDrawerAfterFinish: true,
                            },
                          });

                          return;
                        } else {
                          isIgnoreTaskFinished.current = false;
                        }
                      },
                      className: cx('custom-dialog'),
                    });
                  } else {
                    if (!data.waitTaskFinishedEvent) {
                      if (getCurrentStep() === 0) {
                        try {
                          await handleUploadScreenshot();
                        } catch (e) {
                          console.log('上传图片error', e);
                        }
                      }
                      // 磁震波&&射频 强制动作复测
                      navPage(true);
                      return;
                    } else {
                      isIgnoreTaskFinished.current = false;
                    }
                  }
                } catch (e) {
                  console.error('发生错误：', e);
                }
              })
              .catch(async ({ code }) => {
                isClickedEnd.current = false;
                isIgnoreTaskFinished.current = false;
                // 设备离线和未连接的情况下，依然跳转下一流程
                if (code && [UPPER_STATUS_CODE.DeviceOffline, UPPER_STATUS_CODE.DeviceNotConnected].includes(+code)) {
                  const { data: newServingOrder } = await service.service.getServingOrderByOperator();
                  dispatch(setCurrentServingOrder(newServingOrder) || ({} as any));
                  setTimeout(() => handleNavFromOrderInfo(newServingOrder, nav), 300);
                }
              })
              .finally(() => {
                setIsStoping(false);
                resolve();
              });
          });
        },
      });
    }, [
      currentOrderIdRef,
      dispatch,
      getCurrentProcess,
      getCurrentStep,
      handleUploadScreenshot,
      isClickedEndMassage,
      isDisconnectMachineArm,
      isTaskAllCompleted,
      latestOrder,
      nav,
      navPage,
      setIsClickedEndMassage,
    ])
  );

  // 控制任务开始/暂停
  const handleToggleTaskState = useMemoizedFn(async () => {
    if (isOperateBtnDisabled) return;

    if (isStepperValueChanging.current) {
      // 当前正在改变参数，禁用暂停按钮
      return;
    }

    try {
      setIsToggleTaskStateLoading(true);

      // 是否在确认按摩头确认流程
      if (massageState === 'Preparing_WaitingInstallation') {
        await socketInstance.current.completeInstallation();
        return;
      }

      if (['Running', 'Running_Massaging'].includes(getMassageState())) {
        await socketInstance.current.pause();
      }
      if (getMassageState() === 'Paused') {
        // 自定义画线不允许继续
        if (latestOrder.current?.modelConfig?.[0]?.value === 'custom-draw-line' && getCurrentStep() === 0) {
          if (isDisconnectMachineArm || isTaskAllCompleted || isClickedEndMassage || isStoping) return;

          // 告诉主程用户当前点击了继续，用于播报相关语音
          socketInstance.current.resume();
          await Dialog.confirm({
            title: '确认要结束服务吗？',
            content: (
              <div>
                在自定义画线方案下，暂不支持暂停继续
                <br />
                请「结束服务」后重新下单。
              </div>
            ),
            confirmText: '确认结束',
            onConfirm: async () => {
              isIgnoreTaskFinished.current = true;
              isClickedEnd.current = true;

              return new Promise(resolve => {
                service.massage
                  .quitMassage({
                    terminate: false,
                    serviceOrderId: currentOrderIdRef.current,
                  })
                  .then(async response => {
                    setIsClickedEndMassage(true);

                    try {
                      const { data } = response || {};
                      // 正式进入了按摩流程才进行评价抽屉弹出（手诊结束时），否则orderId传递为null 不进行弹窗

                      const finalOrderId = getCurrentProcess() === 'Massage' ? currentOrderIdRef.current : null;
                      if (
                        latestOrder.current?.productItems?.includes('Shockwave') &&
                        getCurrentProcess() === 'Massage' &&
                        (!!data?.probeMaxUseCount || !!data?.orderMaxUseCount)
                      ) {
                        Dialog.alert({
                          title: '磁震波设备保养提示',
                          content:
                            latestOrder.current?.shockwaveType === 1 ? (
                              <div>
                                <span>磁震波每击打{data?.probeMaxUseCount / 10000}万次需要保养，</span>
                                <br />
                                <span>
                                  当前设备已击打
                                  <span style={{ color: '#05f' }}>{data?.shockwaveProbeCount}</span>次,
                                </span>
                                <br />
                                <span>若不处理会影响顾客的理疗体验，</span>
                                <br />
                                <span>建议您及时保养。有任何问题可以联系客服。</span>
                              </div>
                            ) : (
                              <div>
                                <span>当前设备已达到较高打击次数</span>
                                <br />
                                <span>为了不影响顾客的理疗体验，建议您及时保养， </span>
                                <br />
                                <span>有任何问题可以联系客服。</span>
                              </div>
                            ),
                          confirmText: '我知道了',
                          onConfirm: async () => {
                            if (!data.waitTaskFinishedEvent) {
                              if (getCurrentStep() === 0) {
                                try {
                                  await handleUploadScreenshot();
                                } catch (e) {
                                  console.log('上传图片error', e);
                                }
                              }

                              nav(ALL_PAGES.ACTION_RETEST, {
                                state: {
                                  isAllProcessSuccess: false,
                                  orderId: finalOrderId,
                                },
                              });

                              return;
                            } else {
                              isIgnoreTaskFinished.current = false;
                            }
                          },
                          className: cx('custom-dialog'),
                        });
                      } else {
                        if (!data.waitTaskFinishedEvent) {
                          if (getCurrentStep() === 0) {
                            try {
                              await handleUploadScreenshot();
                            } catch (e) {
                              console.log('上传图片error', e);
                            }
                          }
                          // 只有磁震波才有动作复测
                          if (
                            latestOrder.current?.productItems?.includes('Shockwave') ||
                            latestOrder.current?.productItems?.includes('RadioFrequency')
                          ) {
                            const productItems = latestOrder?.current?.productItems;
                            nav(
                              productItems?.includes('Shockwave')
                                ? ALL_PAGES.ACTION_RETEST
                                : ALL_PAGES.BODY_REMEASUREMENT,
                              {
                                state: {
                                  isAllProcessSuccess: false,
                                  orderId: finalOrderId,
                                },
                              }
                            );
                          } else {
                            nav(ALL_PAGES.MEMBER_DETAIL, {
                              state: { orderId: finalOrderId },
                            });
                          }
                          return;
                        } else {
                          isIgnoreTaskFinished.current = false;
                        }
                      }
                    } catch (e) {
                      console.error('发生错误：', e);
                    }
                  })
                  .catch(async ({ code }) => {
                    isClickedEnd.current = false;
                    isIgnoreTaskFinished.current = false;
                    // 设备离线和未连接的情况下，依然跳转下一流程
                    if (
                      code &&
                      [UPPER_STATUS_CODE.DeviceOffline, UPPER_STATUS_CODE.DeviceNotConnected].includes(+code)
                    ) {
                      const { data: newServingOrder } = await service.service.getServingOrderByOperator();
                      dispatch(setCurrentServingOrder(newServingOrder) || ({} as any));
                      setTimeout(() => handleNavFromOrderInfo(newServingOrder, nav), 300);
                    }
                  })
                  .finally(() => {
                    setIsStoping(false);
                    resolve();
                  });
              });
            },
          });
        } else {
          await socketInstance.current.resume();
        }
      }
      if (
        getMassageState() === 'Idle' ||
        getMassageState() === 'Preparing' ||
        getMassageState() === 'Finished' ||
        !getMassageState()
      ) {
        await service.lanInterface.runTask({
          taskId: getSteps()[getCurrentStep()]?.taskId,
          deviceId: currentServingOrder?.deviceId,
        });
        updateTaskSequenceStatus();
      }
    } finally {
      setTimeout(() => setIsToggleTaskStateLoading(false), 1000);
    }
  });

  // 当前功能调节配置项
  const funcAdjustControlItems = useMemo(
    () => controlPanelData[currentStep]?.controlItemConfigs?.filter(item => FunctionAdjustItems.includes(item.name)),
    [controlPanelData, currentStep]
  );

  // 当前所有理疗步骤
  const allRobotSteps = useMemo(
    () =>
      actionSteps?.length ? actionSteps : funcAdjustControlItems?.find(item => item.name === 'robot.step')?.values,
    [actionSteps, funcAdjustControlItems]
  );
  // 当前按摩步骤索引
  const currentStepIndex = useMemo(
    () => allRobotSteps?.findIndex(item => String(item.value) === String(allValues['robot.step'])),
    [allRobotSteps, allValues]
  );

  // 当参数能调节配置项
  const paramsAdjustControlItems = useMemo(
    () => controlPanelData[currentStep]?.controlItemConfigs?.filter(item => !FunctionAdjustItems.includes(item.name)),
    [controlPanelData, currentStep]
  );

  // 获取服务中用户信息 不支持编辑
  const getServiceCustomerInfo = useCallback(
    async customerId => {
      sendEvent?.(TrackerCustomEvent.ViewMemberInfoClk, { et: 'CLK', c1: ViewMemberInfoScene.Massage });
      const { data } = await service.customer.getCustomerProfile(customerId);
      setCurrentCustomerInfo(data);
      setIsDrawerShow(true);
    },
    [sendEvent]
  );

  // 手诊流程结束后，需要更新的状态数据
  const handleOnSuccessPalpation = useCallback(async () => {
    getBodyNodeAndLineList();
    // 更新控制面板参数
    const { data: panelData } = await service.massage.getMassageControlPannel(currentOrderIdRef.current);
    setControlPanelData(convertObjectKeysToCamelCase(panelData));

    // fix: 手诊完成后如有按摩痛点，更新actions
    if (isNewSoftwareVersion(latestOrder?.current?.device?.softwareVersion, '1.6.5')) {
      try {
        const dynamicActions = await socketInstance.current.getActions();
        setDynamicActions(convertObjectKeysToCamelCase(dynamicActions?.actions));
      } catch (e) {
        console.error(e);
      }
    }

    const { data: taskInfo } = await service.massage.getMassageTaskInfo(currentOrderIdRef.current);
    setSteps(taskInfo);
  }, [
    currentOrderIdRef,
    getBodyNodeAndLineList,
    latestOrder,
    setControlPanelData,
    setDynamicActions,
    setSteps,
    socketInstance,
  ]);

  const realModelSolution = useMemo(() => {
    // return 'shock-waist-deji';
    // 1.6.5及以上版本才需要升级动态方案
    if (isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.6.5')) {
      // 人工治疗不升级动效
      if (steps[currentStep]?.massageType === 'ManualMassage') {
        return currentModelSolution;
      }

      // 方案是否要升级
      if (shouldUpgradeModelSolutions.includes(currentModelSolution)) {
        // 方案名是否要加 dynamic
        if (currentModelSolution.includes('dynamic')) {
          return currentModelSolution;
        } else {
          return (currentModelSolution + '-dynamic') as Massage.DynamicSolution;
        }
      } else {
        return currentModelSolution;
      }
    } else {
      return currentModelSolution;
    }
  }, [currentModelSolution, currentServingOrder?.device?.softwareVersion, currentStep, steps]);

  const currentStepText = useMemo(
    () =>
      currentStep > 0
        ? '人工辅助理疗阶段'
        : intensifyTreatmentStatus?.is_intensifying
        ? '局部加强'
        : allRobotSteps?.find(item => String(item.value) === String(allValues['robot.step']))?.label || '',
    [allRobotSteps, allValues, currentStep, intensifyTreatmentStatus?.is_intensifying]
  );

  const nextStepText = useMemo(() => {
    if (currentStepIndex < allRobotSteps?.length - 1) {
      return (
        allRobotSteps?.[intensifyTreatmentStatus?.is_intensifying ? currentStepIndex : currentStepIndex + 1]?.label ||
        ''
      );
    } else {
      return '';
    }
  }, [allRobotSteps, currentStepIndex, intensifyTreatmentStatus?.is_intensifying]);

  // 当前正在执行动态按摩方案的index
  const currentDynamicRunningIndex = useMemo(
    () => dynamicActions?.findIndex(item => item?.state === 'RUNNING'),
    [dynamicActions]
  );

  const handleStepSkip = useLockFn(
    useCallback(async () => {
      if (isStepperValueChanging.current) {
        // 当前正在改变参数，禁用跳过按钮
        return;
      }

      if (
        isDisconnectMachineArm ||
        !isSocketConnected ||
        isSkipBtnDisabled ||
        !funcAdjustControlItems?.find(item => item.name === 'robot.step')
      ) {
        return;
      }

      // 最后一步跳过时提示
      if (currentStepIndex === allRobotSteps?.length - 1 && !intensifyTreatmentStatus?.is_intensifying) {
        if (isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.6.5')) {
          try {
            await Dialog.confirm({
              title: '确认要跳过最后一步吗？',
              content: (
                <div>
                  跳过后将结束当前理疗方案，
                  <br />
                  并进入下一环节。
                </div>
              ),
              confirmText: '确认跳过',
              onConfirm: async () => {
                isClickedEnd.current = true;
                try {
                  setIsSkipping(true);
                  await socketInstance.current.skip();
                } catch {
                  isClickedEnd.current = false;
                } finally {
                  setTimeout(() => setIsSkipping(false), 5000);
                }
              },
            });
          } catch {}
        } else {
          Toast.show('当前是理疗最后一步，不可跳过，可手动结束服务');
        }
        return;
      }

      try {
        setIsSkipping(true);
        await socketInstance.current.skip();
      } finally {
        setTimeout(() => setIsSkipping(false), 800);
      }
    }, [
      allRobotSteps?.length,
      currentServingOrder?.device?.softwareVersion,
      currentStepIndex,
      funcAdjustControlItems,
      intensifyTreatmentStatus?.is_intensifying,
      isDisconnectMachineArm,
      isSkipBtnDisabled,
      isSocketConnected,
      socketInstance,
    ])
  );

  // 页面兼容长按逻辑处理
  /** 跳过 */
  useLongPress(() => {}, skipBtnRef, { onClick: handleStepSkip, onLongPressEnd: handleStepSkip });
  /** 结束 */
  useLongPress(() => {}, endBtnRef.current?.nativeElement, { onClick: endService, onLongPressEnd: endService });
  /** 暂停/继续 */
  useLongPress(() => {}, operateBtnRef.current?.nativeElement, {
    onClick: handleToggleTaskState,
    onLongPressEnd: handleToggleTaskState,
  });

  useEffect(() => {
    initCurrentTaskAndSocket();
    return () => {
      toastify.clear();
      Dialog.clear();
      clearTimeout(palpationTimeoutTimer.current);
      PalpationEventBus.removeAllListeners();
      destroySocket();
      lookie.remove(CURRENT_ORDER_ID);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentServingOrder?.orderId && isSocketConnected) {
      getMassageTask(currentServingOrder.orderId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentServingOrder?.orderId, isSocketConnected]);

  // 渲染磁震波预设路线
  useEffect(() => {
    // 等模型渲染出来在再渲染预设路线
    if (
      isRenderModelOK &&
      isRenderPointOK &&
      dynamicActions?.length &&
      currentProcess === 'Massage' &&
      currentServingOrder?.modelConfig?.[0]?.key === 'Shockwave'
    ) {
      model2dRef.current?.renderPreSetSplineHitInfo(
        // 过滤标记的手诊痛点
        dynamicActions
          ?.filter(item => item.target === null)
          ?.map(item => ({ lineId: item.actionId, state: item.state as any }))
      );
    }

    if (currentProcess !== 'Massage') {
      setIsRenderModelOK(false);
      setIsRenderPointOK(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRenderModelOK, isRenderPointOK, dynamicActions?.length, currentProcess, currentServingOrder]);

  // 动态渲染自定义走线状态
  useEffect(() => {
    if (
      isRenderModelOKWithoutTimeout &&
      customLineDrawInfo?.length &&
      currentProcess === 'Massage' &&
      currentServingOrder?.modelConfig?.[0]?.value === 'custom-draw-line'
    ) {
      model2dRef.current?.renderCustomLineCoords(customLineDrawInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRenderModelOKWithoutTimeout, customLineDrawInfo, currentProcess, currentServingOrder?.orderId]);

  // 动态渲染矩阵式艾灸区域
  useEffect(() => {
    if (isRenderModelOK && storeMoxibustionCompletedAreas && currentProcess === 'Massage') {
      renderMoxibustionAreas(storeMoxibustionCompletedAreas);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRenderModelOK, storeMoxibustionCompletedAreas, currentProcess]);

  // 绘制动态渲染方案
  useEffect(() => {
    // 1.6.5以下机器不展示动效
    if (!isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.6.5')) {
      return;
    }

    // 人工单不展示动效
    if (steps[currentStep]?.massageType === 'ManualMassage') {
      return;
    }

    if (isRenderModelOK && currentModelSolution) {
      // 渲染冲击波模型痛点数据
      if (
        (currentModelSolution === 'custom-draw-line' &&
          isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.8.10')) ||
        latestOrder.current?.modelConfig?.[0]?.key === 'Shockwave'
      ) {
        model2dRef.current?.handleRenderMassageNodule(dynamicActions);
        return;
      }

      if (shoulderHaveDynamicActionsPlan.includes(currentModelSolution) && !dynamicActions?.length) {
        return;
      }

      // 去除自定义痛点actions后判断实际按摩类型
      const actionsWithoutNodule = dynamicActions?.filter(item => item?.target?.type !== 'NODULE');
      if (currentModelSolution === 'massage-yzc') {
        if (actionsWithoutNodule?.length === 16) {
          model2dRef.current?.drawDynamicSolution('massage-yzc-16-dynamic', dynamicActions);
        } else if (actionsWithoutNodule?.length === 18) {
          model2dRef.current?.drawDynamicSolution('massage-yzc-18-dynamic', dynamicActions);
        }
      } else if (currentModelSolution === 'massage-yzc-10-points') {
        model2dRef.current?.drawDynamicSolution('massage-yzc-10-dynamic', dynamicActions);
      } else if (
        [
          'massage-yzc-zhai-dynamic',
          'massage-shoulder-lv-dynamic',
          'massage-shoulder-lv-v2-dynamic',
          'massage-waist-lv-dynamic',
        ].includes(currentModelSolution)
      ) {
        model2dRef.current?.drawDynamicSolution(currentModelSolution as Massage.DynamicSolution, dynamicActions);
      } else if (shouldUpgradeModelSolutions.includes(currentModelSolution)) {
        model2dRef.current?.drawDynamicSolution(
          (currentModelSolution + '-dynamic') as Massage.DynamicSolution,
          dynamicActions
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isRenderModelOK,
    steps?.length,
    currentDynamicRunningIndex,
    currentModelSolution,
    currentServingOrder?.device?.softwareVersion,
    currentStep,
  ]);

  // eslint-disable-next-line fuxi/require-useEffect-before-return
  useEffect(() => {
    if (steps?.length && currentStep === 0 && isRenderModelOK && currentProcess === 'Massage') {
      if (!isFirstUploadScreenshot.current) {
        isFirstUploadScreenshot.current = true;
        setTimeout(() => handleUploadScreenshot(), 2000);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRenderModelOK, steps?.length, currentStep, currentProcess]);

  // 初始化监听屏幕状态
  useEffect(() => {
    const handler = App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        // 熄屏时 防止动效数据使用缓存数据 需清空状态
        setDynamicActions([]);
        model2dRef.current?.resetDynamicSolution();
      }
    });
    return () => {
      handler.then(h => h.remove());
    };
  }, []);

  return (
    <MassageControlValueContext.Provider
      value={{
        massageState,
        allValues,
        setAllValues,
        getAllValues,
        productInfo,
        currentMassageType: steps?.[currentStep]?.massageType,
        isDisconnectMachineArm,
        currentStepText,
        isSocketConnected,
      }}
    >
      <div className={cx('massage-control')}>
        {currentProcess ? (
          <>
            <CheckArm ref={checkArmRef} />
            <PhysicalTherapyDrawer
              socketInstance={socketInstance}
              ref={physicalTherapyDrawerRef}
              exposedAreaCode={currentExposedAreaCode}
            />
            <RobotExceptionPrompt
              currentProcess={currentProcess}
              socketInstance={socketInstance}
              ref={robotExceptionPromptRef}
              isIgnoreTaskFinished={isIgnoreTaskFinished}
            />
            {currentServingOrder?.modelConfig?.[0]?.key === 'RadioFrequency' && <HeatTip ref={heatTipRef} />}
            <Header
              title={
                <HeaderMemberInfo
                  name={`${currentServingOrder?.customerName}`}
                  handleClick={() => {
                    if (getCurrentProcess() === 'Massage') {
                      getServiceCustomerInfo(currentServingOrder?.customerId);
                    }
                  }}
                  avatarUrl={currentServingOrder?.avatarUrl ?? currentServingOrder?.userAvatar}
                />
              }
              backConfig={{ text: '会员详情', action: () => nav(ALL_PAGES.MEMBER_DETAIL) }}
            />

            <MemberDrawer
              open={isDrawerShow}
              memberInfo={currentCustomerInfo}
              handleClose={() => setIsDrawerShow(false)}
              isServicing={true}
            />

            {/* 安装理疗末端 && 末端走线（仅热砭石，磁震波，内源热能）*/}
            {['Installation']?.includes(currentProcess) ? (
              <InstallMassageHead
                isDisconnectMachineArm={isDisconnectMachineArm}
                currentProcess={currentProcess}
                isStoping={isStoping}
                endService={endService}
                currentTreatType={isMatrizMoxibustionPlan ? 'MatrixMoxibustion' : steps[currentStep]?.massageItem}
                socketInstance={socketInstance}
              />
            ) : // 手诊和人体检测
            ['VisualCheck', 'VisualCheckAndPalpation', 'CustomPalpation'].includes(currentProcess) ? (
              <div className={cx('visual-check')}>
                <Palpation
                  isDisconnectMachineArm={isDisconnectMachineArm}
                  onSuccessPalpation={handleOnSuccessPalpation}
                  endService={endService}
                  isStoping={isStoping}
                  socketInstance={socketInstance}
                  isSocketConnected={isSocketConnected}
                  lineHits={lineHits}
                />
              </div>
            ) : (
              // 开始按摩
              <div className={cx('view-wrapper')}>
                <div className={cx('left')}>
                  <div className={cx('model-wrapper')}>
                    <div className={cx('plan-details-info-box')}>
                      <div className={cx('plan-header', { expand: isShowPlanDetails })}>
                        <div className={cx('title')}>
                          <div className={cx('name')}>{productInfo?.name}</div>
                          <When
                            condition={isShowPlanDetails}
                            fallback={
                              <div className={cx('massage-type')}>
                                <span className={cx('massage-type-text', currentServingOrder?.productItems?.[0])}>
                                  {MassageType2Text[currentServingOrder?.productItems?.[0]]}
                                </span>
                              </div>
                            }
                          >
                            <div className={cx('plan-details-text')}>方案详情</div>
                          </When>
                          <When condition={!currentServingOrder?.onlyManual}>
                            <Icon
                              name="toggle"
                              className={cx('toggle-icon')}
                              onClick={() => {
                                sendEvent?.(TrackerCustomEvent.MassagePlanDetailsClk, {
                                  et: 'CLK',
                                  c1: currentServingOrder?.productItems?.[0],
                                });
                                setIsShowPlanDetails(!isShowPlanDetails);
                              }}
                            />
                          </When>
                        </div>
                      </div>
                      <div className={cx('tech-intro', { show: isShowPlanDetails, expand: isShowPlanDetails })}>
                        <div className={cx('intro-label')}>理疗手法：</div>
                        <div className={cx('intro-value')}>
                          {/* 文本通过换行符进行分割 */}
                          {productInfo?.massageTech?.split('\n')?.map((item, index) =>
                            index === 0 ? (
                              <span key={index}>{item}</span>
                            ) : (
                              <span key={index}>
                                <br />
                                {item}
                              </span>
                            )
                          )}
                        </div>
                      </div>

                      <div className={cx('plan-details-imgs-box', { show: isShowPlanDetails })}>
                        {currentServingOrder?.productItems?.[0] === 'Moxibustion' ? (
                          <MoxPlanDetails renderAreas={storeMoxibustionCompletedAreas?.map(item => item.area)} />
                        ) : (
                          <When
                            condition={
                              (!!productInfo?.maleProductDescUrls?.length || !!productInfo?.productDescUrls?.length) &&
                              steps?.[currentStep]?.massageType === 'Massage'
                            }
                          >
                            <SlideImgs
                              imgs={
                                currentServingOrder?.customerGender === 'Male' &&
                                productInfo?.maleProductDescUrls?.length > 0
                                  ? productInfo?.maleProductDescUrls
                                  : productInfo?.productDescUrls
                              }
                            />
                          </When>
                        )}
                      </div>
                    </div>
                    <Model2D
                      isInIntensifying={!!intensifyTreatmentStatus?.is_intensifying}
                      ref={model2dRef}
                      modelSolution={realModelSolution}
                      onRenderPointOK={() => setTimeout(() => setIsRenderPointOK(true), 2000)}
                      onRenderModelOK={() => {
                        setIsRenderModelOKWithoutTimeout(true);
                        setTimeout(() => setIsRenderModelOK(true), 1500);
                      }}
                      bodyNodeList={bodyNodeList}
                    />
                  </div>
                  <div className={cx('plan-info')}>
                    <div className={cx('plan-info-left')}>
                      <div className={cx('plan-info-header')}>
                        <div className={cx('step-name')}>
                          <div className={cx('current-step')}>{currentStepText}</div>
                          <div className={cx('next-step')}>{nextStepText}</div>
                        </div>
                        {!!(progressInfo?.remaining || intensifyTreatmentStatus?.remaining_duration) && (
                          <div className={cx('remaining')}>
                            {isShowNewProcess && taskSequence?.task_sequence?.[1]?.state !== 'Created' ? (
                              currentStepIsShockwave ? (
                                <div className={cx('retaining-manual-box')}>
                                  <span className={cx('retaining-shockwave')}>剩余{progressInfo?.remaining}次/</span>
                                  <span className={cx('beaten-shockwave')}>
                                    已击打{progressInfo?.order_actual_shockwave_hits}次
                                  </span>
                                </div>
                              ) : (
                                <div className={cx('retaining-manual-box')}>
                                  <span className={cx('retaining-shockwave')}>
                                    已理疗{formatSeconds(progressInfo?.order_massage_duration)}
                                  </span>
                                </div>
                              )
                            ) : (
                              <div className={cx('remaining-machine-box')}>
                                {currentStepIsShockwave && steps?.[currentStep]?.massageType !== 'ManualMassage' && (
                                  <span>{isShowIntensifyUIState ? '' : currentIsHitLine ? '走线' : '痛点'}</span>
                                )}
                                <span>剩余</span>
                                <span className={cx('num', { 'no-gap': currentStepIsShockwave })}>
                                  {isShowIntensifyUIState
                                    ? formatSeconds(intensifyTreatmentStatus.remaining_duration)
                                    : currentStepIsShockwave
                                    ? /**
                                       *  1.8.0 以下版本的机器 remaining_line 和 remaining_nodule 字段有误
                                       *  在 1.8.0 及以上版本修正
                                       */
                                      isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.8.0') &&
                                      // 人工单取remaining字段
                                      !currentServingOrder?.onlyManual
                                      ? currentIsHitLine
                                        ? progressInfo?.remaining_line
                                        : progressInfo?.remaining_nodule
                                      : progressInfo?.remaining
                                    : formatSeconds(progressInfo?.remaining)}
                                </span>
                                {currentStepIsShockwave && <span className={cx('unit')}>次</span>}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className={cx('plan-info-footer')}>
                        {['Running_Massaging', 'Idle', 'Finishing', 'Finished'].includes(massageState) ||
                        controlFooterContentShow === 'progress' ? (
                          <div
                            className={cx('hits-infos')}
                            onClick={() => massageState === 'Paused' && setControlFooterContentShow('pause-tips')}
                          >
                            <div className={cx('machine-treat')}>
                              <Progress
                                className={cx('progress-wrapper')}
                                percent={
                                  isShowIntensifyUIState
                                    ? (intensifyTreatmentStatus?.remaining_duration /
                                        intensifyTreatmentStatus?.total_duration) *
                                      100
                                    : currentStep > 0
                                    ? 100
                                    : progressInfo?.progress
                                }
                                showInfo={false}
                                size={['100%', 12]}
                                strokeColor={isShowIntensifyUIState ? '#0055FF' : '#fff'}
                                trailColor={isShowIntensifyUIState ? '#D4DFF2' : '#e1e1e1'}
                              />
                              <div>{steps?.[0]?.massageType === 'ManualMassage' ? '人工' : '机器'}治疗</div>
                            </div>
                            {steps?.length > 1 && (
                              <div
                                className={cx('manual-treat', {
                                  'process-bg':
                                    isShowNewProcess && taskSequence?.task_sequence?.[1]?.state === 'Started',
                                })}
                              >
                                <Progress
                                  className={cx('progress-wrapper')}
                                  percent={
                                    isShowNewProcess && taskSequence?.task_sequence?.[1]?.state === 'Started'
                                      ? 100
                                      : currentStep === 1
                                      ? progressInfo?.progress
                                      : 0
                                  }
                                  showInfo={false}
                                  size={['100%', 12]}
                                  strokeColor="#fff"
                                  trailColor="#e1e1e1"
                                />
                                <div className={cx('line')} />
                                {/* 机器版本大于等于1.7.0的版本显示新交互 */}
                                {isShowNewProcess && (
                                  <>
                                    {taskSequence?.task_sequence?.[1]?.state === 'Created' &&
                                      getMassageState() === 'Idle' && (
                                        <>
                                          {currentStepIsShockwave ? (
                                            <>
                                              <div className={cx('common')}>
                                                已击打{progressInfo?.order_actual_shockwave_hits}次
                                              </div>
                                              <div className={cx('triangle')} />
                                            </>
                                          ) : (
                                            <>
                                              <div className={cx('common')}>
                                                已理疗{formatSeconds(progressInfo?.order_massage_duration)}
                                              </div>
                                              <div className={cx('triangle')} />
                                            </>
                                          )}
                                        </>
                                      )}
                                    {taskSequence?.task_sequence?.[1]?.state === 'Started' && (
                                      <img alt="process" src={processManualImg} className={cx('process-img')} />
                                    )}
                                  </>
                                )}

                                <div>人工治疗</div>
                              </div>
                            )}
                          </div>
                        ) : ['Paused', 'Pausing'].includes(massageState) ||
                          controlFooterContentShow === 'pause-tips' ? (
                          <div
                            className={cx('tips')}
                            onClick={() => massageState === 'Paused' && setControlFooterContentShow('progress')}
                          >
                            <Icon name="tips" className={cx('icon')} />
                            <span>免费暂停时长为3分钟，请合理安排时间</span>
                          </div>
                        ) : (
                          <div>&nbsp;</div>
                        )}
                      </div>
                    </div>
                    <div className={cx('plan-info-right')}>
                      {isShowNewProcess &&
                      !currentStepIsShockwave &&
                      ['Created', 'Started']?.includes(taskSequence?.task_sequence?.[1]?.state) ? (
                        <div className={cx('tips')}>
                          <span>建议不超过</span>
                          <br />
                          <span className={cx('num')}>60</span>
                          <span>分钟</span>
                        </div>
                      ) : (
                        <div
                          ref={skipBtnRef}
                          className={cx('skip', {
                            disabled:
                              isDisconnectMachineArm ||
                              !isSocketConnected ||
                              isSkipBtnDisabled ||
                              !funcAdjustControlItems?.find(item => item.name === 'robot.step'),
                          })}
                        >
                          <span>跳过</span>
                          <Icon className={cx('icon')} name="share" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className={cx('right')}>
                  {isSocketConnected ? (
                    <>
                      <SlideSelect
                        steps={[
                          { value: 'func', label: '功能调节' },
                          { value: 'params', label: '参数调节' },
                        ]}
                        value={currentAdjustItem}
                        onChange={value => setCurrentAdjustItem(value as typeof currentAdjustItem)}
                      />
                      <div className={cx('control-box')}>
                        <FuncAdjuster
                          ref={funcAdjusterRef}
                          isSocketConnected={isSocketConnected}
                          socketInstance={socketInstance}
                          bgmState={bgmState}
                          controlItems={funcAdjustControlItems}
                          isShow={currentAdjustItem === 'func'}
                          className={cx('func-adjust', { hide: currentAdjustItem !== 'func' })}
                        />
                        <ParamsAdjuster
                          onStepperValueChange={handleOnStepperValueChange}
                          currentIsHitLine={currentIsHitLine}
                          noduleLength={bodyNodeList?.length}
                          massageTechValues={massageTechValues}
                          massageState={massageState}
                          isSkipping={isSkipping}
                          socketInstance={socketInstance}
                          controlItems={paramsAdjustControlItems}
                          intensifyTreatmentStatus={intensifyTreatmentStatus}
                          lineHits={progressInfo?.total_line}
                          className={cx('params-adjust', { hide: currentAdjustItem !== 'params' })}
                        />
                      </div>
                    </>
                  ) : (
                    <div className={cx('disconnected-dom')}>
                      <img src={disconnectImg} alt="连接失败" />
                      <span className={cx('tip-text')}>机器人断开连接</span>
                    </div>
                  )}
                  <div className={cx('btns')}>
                    <Button
                      ref={endBtnRef}
                      color="default"
                      disabled={isDisconnectMachineArm || isTaskAllCompleted || isClickedEndMassage}
                      loading={isStoping}
                      className={cx('btn', 'btn-danger')}
                    >
                      结束服务
                    </Button>
                    <Button
                      ref={operateBtnRef}
                      color="primary"
                      loading={isToggleTaskStateLoading}
                      className={cx('btn', 'btn-primary')}
                      disabled={
                        currentIsHasL1L2Error ||
                        !isSocketConnected ||
                        isDisconnectMachineArm ||
                        isClickedEndMassage ||
                        steps?.length === 0 ||
                        isStartBtnDisabled ||
                        ![
                          'Running',
                          'Running_WaitingInstallation',
                          'Running_Massaging',
                          'Paused',
                          'Idle',
                          null,
                        ].includes(massageState) ||
                        isTaskAllCompleted ||
                        isOrderError ||
                        !isTaskAlreadyStarted ||
                        isUploadingScreenshot
                      }
                    >
                      {isClickedEndMassage
                        ? '已结束'
                        : isOrderError
                        ? '订单异常'
                        : isTaskAllCompleted
                        ? '已结束'
                        : isTaskAlreadyStarted
                        ? MassageState2Text[massageState] || '开始'
                        : '正在开始'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className={cx('loading-wrapper')}>
            {state?.showSkeleton ? <div className={cx('massage-skeleton')} /> : <Loading3QuartersOutlined spin />}
          </div>
        )}
      </div>
    </MassageControlValueContext.Provider>
  );
};

export default MassageControl;
